import { Dialog, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import { TbPlayerPlay } from "react-icons/tb";
import { useToast } from "../../../../../Hooks/toast";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import {
  BAAS_TRANSACTION_STATUSES,
  BAAS_TRANSACTION_TYPES,
  MOCK_CLIENTS,
  simulateMockBaasTransaction,
} from "../../../../../Services/baasTransactionsApi";

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-lg";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const INITIAL_FORM = {
  type: "PIX",
  amount: "",
  clientId: MOCK_CLIENTS[0]?.id ?? "",
  status: "pending",
  direction: "outbound",
};

export const BaasTransactionSimulateModal = ({ open, onClose, onSuccess }) => {
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (!open) {
      setForm(INITIAL_FORM);
      setLoading(false);
      setFormError(null);
    }
  }, [open]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const amount = Number(String(form.amount).replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) {
      setFormError(intl.get("baas.transactions.simulate.invalidAmount"));
      return;
    }

    setLoading(true);
    setFormError(null);
    await new Promise((resolve) => setTimeout(resolve, 400));

    const result = simulateMockBaasTransaction(selectedInstance?.id, {
      type: form.type,
      amount,
      clientId: form.clientId,
      status: form.status,
      direction: form.direction,
    });

    setLoading(false);

    if (!result) {
      setFormError(intl.get("baas.transactions.simulate.error"));
      return;
    }

    addToast({
      type: "success",
      title: intl.get("baas.transactions.simulate.success"),
    });
    onSuccess?.(result);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: MODAL_CLASS }}
    >
      <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e7a73f]/15">
            <TbPlayerPlay className="h-5 w-5 text-[#a66f1d]" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
              {intl.get("baas.transactions.simulate.title")}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.transactions.simulate.subtitle")}
            </p>
          </div>
        </div>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={loading}
          aria-label={intl.get("baas.transactions.simulate.cancel")}
        >
          <MdClose />
        </IconButton>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 px-4 py-4">
        {formError ? (
          <p
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {formError}
          </p>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200">
              {intl.get("baas.transactions.simulate.type")}
            </span>
            <select
              className={inputClass}
              value={form.type}
              onChange={(event) => updateField("type", event.target.value)}
            >
              {BAAS_TRANSACTION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {intl.get(`baas.transactions.type.${type}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200">
              {intl.get("baas.transactions.simulate.amount")}
            </span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              required
              className={inputClass}
              value={form.amount}
              onChange={(event) => updateField("amount", event.target.value)}
              placeholder="0,00"
            />
          </label>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200">
              {intl.get("baas.transactions.simulate.client")}
            </span>
            <select
              className={inputClass}
              value={form.clientId}
              onChange={(event) => updateField("clientId", event.target.value)}
            >
              {MOCK_CLIENTS.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200">
              {intl.get("baas.transactions.simulate.status")}
            </span>
            <select
              className={inputClass}
              value={form.status}
              onChange={(event) => updateField("status", event.target.value)}
            >
              {BAAS_TRANSACTION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {intl.get(`baas.transactions.status.${status}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200">
              {intl.get("baas.transactions.simulate.direction")}
            </span>
            <select
              className={inputClass}
              value={form.direction}
              onChange={(event) => updateField("direction", event.target.value)}
            >
              <option value="outbound">
                {intl.get("baas.transactions.direction.outbound")}
              </option>
              <option value="inbound">
                {intl.get("baas.transactions.direction.inbound")}
              </option>
            </select>
          </label>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4 dark:border-brand-dark-border">
          <button
            type="button"
            className="btn-outline !h-9 !min-h-9 !px-3"
            onClick={onClose}
            disabled={loading}
          >
            {intl.get("baas.transactions.simulate.cancel")}
          </button>
          <button
            type="submit"
            className="btn-primary !h-9 !min-h-9 !px-4"
            disabled={loading}
          >
            {loading
              ? intl.get("baas.transactions.simulate.processing")
              : intl.get("baas.transactions.simulate.confirm")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};
